/**
 * wizardly.ai - Smart Diagrams
 *
 * Gamma-style diagram renderers for Canvas:
 * - Funnel
 * - Pyramid
 * - Venn (2-3 circles)
 * - Quadrant (2x2 matrix)
 * - Bullseye (target)
 * - Cycle (circular)
 * - Staircase
 * - Chain (connected nodes)
 * - Versus (comparison)
 * - Iceberg
 */

import { renderIcon, selectIconForContent } from './iconRenderer';

/**
 * Render a Funnel diagram
 * Shows decreasing stages (like sales funnel)
 */
export function renderFunnel(ctx, items, x, y, width, height, colorScheme, options = {}) {
  if (!items || items.length === 0) return;

  const {
    showLabels = true,
    showValues = true,
    gradientColors = null
  } = options;

  const itemCount = items.length;
  const itemHeight = height / itemCount;
  const maxWidth = width * 0.9;
  const minWidth = width * 0.3;

  items.forEach((item, i) => {
    // Calculate trapezoid dimensions
    const topWidth = maxWidth - (i * (maxWidth - minWidth) / itemCount);
    const bottomWidth = maxWidth - ((i + 1) * (maxWidth - minWidth) / itemCount);
    const topX = x + (width - topWidth) / 2;
    const bottomX = x + (width - bottomWidth) / 2;
    const topY = y + i * itemHeight;
    const bottomY = y + (i + 1) * itemHeight;

    // Draw trapezoid
    const colors = gradientColors || [
      colorScheme.primary,
      colorScheme.secondary,
      colorScheme.accent,
      adjustColor(colorScheme.primary, -20),
      adjustColor(colorScheme.secondary, -20)
    ];

    ctx.fillStyle = colors[i % colors.length];
    ctx.beginPath();
    ctx.moveTo(topX, topY);
    ctx.lineTo(topX + topWidth, topY);
    ctx.lineTo(bottomX + bottomWidth, bottomY);
    ctx.lineTo(bottomX, bottomY);
    ctx.closePath();
    ctx.fill();

    // Add subtle stroke
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Draw label
    if (showLabels) {
      const text = typeof item === 'string' ? item : (item.label || item.text || `Stage ${i + 1}`);
      const value = typeof item === 'object' ? item.value : null;

      ctx.fillStyle = '#ffffff';
      ctx.font = `600 ${Math.min(24, itemHeight * 0.35)}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const centerY = topY + itemHeight / 2;
      ctx.fillText(text.substring(0, 30), x + width / 2, centerY - (value ? 12 : 0));

      if (showValues && value) {
        ctx.font = `400 ${Math.min(18, itemHeight * 0.25)}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.fillText(value, x + width / 2, centerY + 14);
      }
    }
  });
}

/**
 * Render a Pyramid diagram
 * Shows hierarchical levels (bottom = foundation, top = peak)
 */
export function renderPyramid(ctx, items, x, y, width, height, colorScheme, options = {}) {
  if (!items || items.length === 0) return;

  const {
    inverted = false,
    showLabels = true
  } = options;

  const itemCount = items.length;
  const itemHeight = height / itemCount;
  const maxWidth = width * 0.95;

  // If not inverted, reverse items so first item is at top (smallest)
  const orderedItems = inverted ? [...items] : [...items].reverse();

  orderedItems.forEach((item, i) => {
    const actualIndex = inverted ? i : (itemCount - 1 - i);

    // Calculate widths (narrower at top for normal pyramid)
    const topRatio = inverted ? (i / itemCount) : (1 - (i + 1) / itemCount);
    const bottomRatio = inverted ? ((i + 1) / itemCount) : (1 - i / itemCount);

    const topWidth = maxWidth * (0.1 + 0.9 * topRatio);
    const bottomWidth = maxWidth * (0.1 + 0.9 * bottomRatio);
    const topX = x + (width - topWidth) / 2;
    const bottomX = x + (width - bottomWidth) / 2;
    const topY = y + i * itemHeight;
    const bottomY = y + (i + 1) * itemHeight;

    // Color gradient from top to bottom
    const colors = [
      colorScheme.primary,
      colorScheme.secondary,
      colorScheme.accent,
      adjustColor(colorScheme.primary, -30),
      adjustColor(colorScheme.secondary, -20)
    ];

    ctx.fillStyle = colors[actualIndex % colors.length];
    ctx.beginPath();
    ctx.moveTo(topX, topY);
    ctx.lineTo(topX + topWidth, topY);
    ctx.lineTo(bottomX + bottomWidth, bottomY);
    ctx.lineTo(bottomX, bottomY);
    ctx.closePath();
    ctx.fill();

    // Subtle border
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Label
    if (showLabels) {
      const text = typeof item === 'string' ? item : (item.label || item.text || `Level ${actualIndex + 1}`);

      ctx.fillStyle = '#ffffff';
      ctx.font = `600 ${Math.min(22, itemHeight * 0.4)}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text.substring(0, 25), x + width / 2, topY + itemHeight / 2);
    }
  });
}

/**
 * Render a Venn diagram (2-3 circles with overlaps)
 */
export function renderVenn(ctx, items, x, y, width, height, colorScheme, options = {}) {
  if (!items || items.length < 2) return;

  const circleCount = Math.min(items.length, 3);
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const radius = Math.min(width, height) * 0.35;
  const overlap = radius * 0.4; // How much circles overlap

  // Calculate circle positions
  const positions = [];
  if (circleCount === 2) {
    positions.push({ x: centerX - overlap, y: centerY });
    positions.push({ x: centerX + overlap, y: centerY });
  } else {
    // 3 circles in triangle formation
    const angleOffset = -Math.PI / 2;
    for (let i = 0; i < 3; i++) {
      const angle = angleOffset + (2 * Math.PI * i) / 3;
      positions.push({
        x: centerX + Math.cos(angle) * overlap,
        y: centerY + Math.sin(angle) * overlap
      });
    }
  }

  const colors = [
    hexToRgba(colorScheme.primary, 0.6),
    hexToRgba(colorScheme.secondary, 0.6),
    hexToRgba(colorScheme.accent, 0.6)
  ];

  // Enable blending for overlap effect
  ctx.globalCompositeOperation = 'multiply';

  // Draw circles
  positions.forEach((pos, i) => {
    ctx.fillStyle = colors[i % colors.length];
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
    ctx.fill();
  });

  // Reset composite operation
  ctx.globalCompositeOperation = 'source-over';

  // Draw borders
  positions.forEach((pos, i) => {
    ctx.strokeStyle = colors[i % colors.length].replace(/[\d.]+\)$/, '1)');
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
    ctx.stroke();
  });

  // Draw labels
  const labelPositions = [];
  if (circleCount === 2) {
    labelPositions.push({ x: centerX - radius - overlap / 2, y: centerY });
    labelPositions.push({ x: centerX + radius + overlap / 2, y: centerY });
  } else {
    for (let i = 0; i < 3; i++) {
      const angle = -Math.PI / 2 + (2 * Math.PI * i) / 3;
      labelPositions.push({
        x: centerX + Math.cos(angle) * (radius + overlap),
        y: centerY + Math.sin(angle) * (radius + overlap * 0.8)
      });
    }
  }

  ctx.fillStyle = colorScheme.text;
  ctx.font = '600 20px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  items.slice(0, circleCount).forEach((item, i) => {
    const text = typeof item === 'string' ? item : (item.label || item.text);
    if (text) {
      ctx.fillText(text.substring(0, 20), labelPositions[i].x, labelPositions[i].y);
    }
  });
}

/**
 * Render a Quadrant (2x2 matrix)
 */
export function renderQuadrant(ctx, items, x, y, width, height, colorScheme, options = {}) {
  const {
    labels = { top: '', bottom: '', left: '', right: '' },
    showAxes = true,
    showAxisLabels = true
  } = options;

  const padding = 60;
  const innerX = x + padding;
  const innerY = y + padding;
  const innerWidth = width - padding * 2;
  const innerHeight = height - padding * 2;

  const centerX = innerX + innerWidth / 2;
  const centerY = innerY + innerHeight / 2;

  // Draw quadrant backgrounds
  const quadrantColors = [
    hexToRgba(colorScheme.primary, 0.15),
    hexToRgba(colorScheme.secondary, 0.15),
    hexToRgba(colorScheme.accent, 0.15),
    hexToRgba(adjustColor(colorScheme.primary, 30), 0.15)
  ];

  // Top-left, Top-right, Bottom-left, Bottom-right
  const quadrants = [
    { x: innerX, y: innerY, w: innerWidth / 2, h: innerHeight / 2 },
    { x: centerX, y: innerY, w: innerWidth / 2, h: innerHeight / 2 },
    { x: innerX, y: centerY, w: innerWidth / 2, h: innerHeight / 2 },
    { x: centerX, y: centerY, w: innerWidth / 2, h: innerHeight / 2 }
  ];

  quadrants.forEach((q, i) => {
    ctx.fillStyle = quadrantColors[i];
    ctx.fillRect(q.x, q.y, q.w, q.h);
  });

  // Draw axes
  if (showAxes) {
    ctx.strokeStyle = colorScheme.textMuted || '#888';
    ctx.lineWidth = 2;

    // Vertical axis
    ctx.beginPath();
    ctx.moveTo(centerX, innerY - 20);
    ctx.lineTo(centerX, innerY + innerHeight + 20);
    ctx.stroke();

    // Horizontal axis
    ctx.beginPath();
    ctx.moveTo(innerX - 20, centerY);
    ctx.lineTo(innerX + innerWidth + 20, centerY);
    ctx.stroke();

    // Arrow heads
    drawArrowHead(ctx, centerX, innerY - 20, 'up');
    drawArrowHead(ctx, innerX + innerWidth + 20, centerY, 'right');
  }

  // Draw axis labels
  if (showAxisLabels) {
    ctx.fillStyle = colorScheme.textMuted || '#888';
    ctx.font = '500 16px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';

    if (labels.top) ctx.fillText(labels.top, centerX, innerY - 35);
    if (labels.bottom) ctx.fillText(labels.bottom, centerX, innerY + innerHeight + 40);

    ctx.save();
    ctx.translate(innerX - 40, centerY);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    if (labels.left) ctx.fillText(labels.left, 0, 0);
    ctx.restore();

    ctx.save();
    ctx.translate(innerX + innerWidth + 40, centerY);
    ctx.rotate(Math.PI / 2);
    ctx.textAlign = 'center';
    if (labels.right) ctx.fillText(labels.right, 0, 0);
    ctx.restore();
  }

  // Draw items in quadrants
  if (items && items.length > 0) {
    ctx.fillStyle = colorScheme.text;
    ctx.font = '600 18px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    items.slice(0, 4).forEach((item, i) => {
      const q = quadrants[i];
      const text = typeof item === 'string' ? item : (item.label || item.text);
      if (text) {
        // Word wrap in quadrant
        const lines = wrapText(ctx, text, q.w - 20, 18);
        const lineHeight = 24;
        const startY = q.y + q.h / 2 - ((lines.length - 1) * lineHeight) / 2;

        lines.forEach((line, li) => {
          ctx.fillText(line, q.x + q.w / 2, startY + li * lineHeight);
        });
      }
    });
  }
}

/**
 * Render a Bullseye (target) diagram
 */
export function renderBullseye(ctx, items, x, y, width, height, colorScheme, options = {}) {
  if (!items || items.length === 0) return;

  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const maxRadius = Math.min(width, height) * 0.45;
  const ringCount = Math.min(items.length, 5);

  // Draw rings from outside to inside
  for (let i = ringCount - 1; i >= 0; i--) {
    const radius = maxRadius * ((i + 1) / ringCount);

    // Gradient colors from outside (lighter) to center (primary)
    const intensity = 1 - (i / ringCount) * 0.7;
    ctx.fillStyle = hexToRgba(colorScheme.primary, 0.3 + intensity * 0.5);

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fill();

    // Ring border
    ctx.strokeStyle = hexToRgba(colorScheme.primary, 0.5);
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  // Draw labels
  ctx.fillStyle = colorScheme.text;
  ctx.font = '600 16px Inter, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  items.slice(0, ringCount).forEach((item, i) => {
    const text = typeof item === 'string' ? item : (item.label || item.text);
    const radius = maxRadius * ((i + 0.5) / ringCount);

    // Position label to the right of center
    const labelX = centerX + maxRadius + 30;
    const labelY = centerY - maxRadius + (i + 0.5) * (maxRadius * 2 / ringCount);

    // Draw connecting line
    ctx.strokeStyle = colorScheme.textMuted || '#888';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(centerX + radius * 0.7, centerY);
    ctx.lineTo(labelX - 10, labelY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw label
    ctx.fillStyle = colorScheme.text;
    ctx.fillText(text ? text.substring(0, 25) : `Ring ${i + 1}`, labelX, labelY);
  });
}

/**
 * Render a Cycle diagram (circular process)
 */
export function renderCycle(ctx, items, x, y, width, height, colorScheme, options = {}) {
  if (!items || items.length < 2) return;

  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const radius = Math.min(width, height) * 0.35;
  const nodeRadius = 40;
  const itemCount = items.length;

  const colors = [
    colorScheme.primary,
    colorScheme.secondary,
    colorScheme.accent,
    adjustColor(colorScheme.primary, -20),
    adjustColor(colorScheme.secondary, -20)
  ];

  // Draw connecting arrows
  ctx.strokeStyle = colorScheme.textMuted || '#888';
  ctx.lineWidth = 3;

  for (let i = 0; i < itemCount; i++) {
    const startAngle = (2 * Math.PI * i) / itemCount - Math.PI / 2;
    const endAngle = (2 * Math.PI * (i + 1)) / itemCount - Math.PI / 2;

    const startX = centerX + Math.cos(startAngle) * radius;
    const startY = centerY + Math.sin(startAngle) * radius;
    const endX = centerX + Math.cos(endAngle) * radius;
    const endY = centerY + Math.sin(endAngle) * radius;

    // Draw arc arrow
    const arcRadius = radius * 0.7;
    ctx.beginPath();
    ctx.arc(centerX, centerY, arcRadius, startAngle + 0.3, endAngle - 0.3);
    ctx.stroke();

    // Arrow head at end
    const arrowAngle = endAngle - 0.3;
    const arrowX = centerX + Math.cos(arrowAngle) * arcRadius;
    const arrowY = centerY + Math.sin(arrowAngle) * arcRadius;
    drawArrowHeadAtAngle(ctx, arrowX, arrowY, arrowAngle + Math.PI / 2, colorScheme.textMuted || '#888');
  }

  // Draw nodes
  items.forEach((item, i) => {
    const angle = (2 * Math.PI * i) / itemCount - Math.PI / 2;
    const nodeX = centerX + Math.cos(angle) * radius;
    const nodeY = centerY + Math.sin(angle) * radius;

    // Node circle
    ctx.fillStyle = colors[i % colors.length];
    ctx.beginPath();
    ctx.arc(nodeX, nodeY, nodeRadius, 0, Math.PI * 2);
    ctx.fill();

    // Node border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Node number
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 20px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((i + 1).toString(), nodeX, nodeY);

    // Label outside
    const text = typeof item === 'string' ? item : (item.label || item.text);
    if (text) {
      const labelRadius = radius + nodeRadius + 25;
      const labelX = centerX + Math.cos(angle) * labelRadius;
      const labelY = centerY + Math.sin(angle) * labelRadius;

      ctx.fillStyle = colorScheme.text;
      ctx.font = '500 16px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(text.substring(0, 20), labelX, labelY);
    }
  });
}

/**
 * Render a Staircase diagram
 */
export function renderStaircase(ctx, items, x, y, width, height, colorScheme, options = {}) {
  if (!items || items.length === 0) return;

  const { ascending = true } = options;
  const itemCount = items.length;
  const stepWidth = (width * 0.85) / itemCount;
  const maxStepHeight = height * 0.7;

  const colors = [
    colorScheme.primary,
    colorScheme.secondary,
    colorScheme.accent,
    adjustColor(colorScheme.primary, -15),
    adjustColor(colorScheme.secondary, -15)
  ];

  items.forEach((item, i) => {
    const index = ascending ? i : (itemCount - 1 - i);
    const stepHeight = maxStepHeight * ((index + 1) / itemCount);
    const stepX = x + (width * 0.075) + i * stepWidth;
    const stepY = y + height - stepHeight - 40;

    // Draw step
    ctx.fillStyle = colors[i % colors.length];
    roundRect(ctx, stepX, stepY, stepWidth - 10, stepHeight, 8);
    ctx.fill();

    // Step number
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 24px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText((i + 1).toString(), stepX + stepWidth / 2 - 5, stepY + 15);

    // Label below or on step
    const text = typeof item === 'string' ? item : (item.label || item.text);
    if (text) {
      ctx.fillStyle = '#ffffff';
      ctx.font = '500 14px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';

      const lines = wrapText(ctx, text, stepWidth - 20, 14);
      lines.slice(0, 3).forEach((line, li) => {
        ctx.fillText(line, stepX + stepWidth / 2 - 5, stepY + 50 + li * 18);
      });
    }
  });
}

/**
 * Render a Chain diagram (connected nodes)
 */
export function renderChain(ctx, items, x, y, width, height, colorScheme, options = {}) {
  if (!items || items.length === 0) return;

  const itemCount = items.length;
  const nodeSize = Math.min(80, (width - 100) / itemCount - 20);
  const nodeSpacing = (width - nodeSize) / Math.max(itemCount - 1, 1);
  const centerY = y + height / 2;

  const colors = [
    colorScheme.primary,
    colorScheme.secondary,
    colorScheme.accent,
    adjustColor(colorScheme.primary, -20),
    adjustColor(colorScheme.secondary, -15)
  ];

  // Draw connecting chain links
  for (let i = 0; i < itemCount - 1; i++) {
    const startX = x + i * nodeSpacing + nodeSize;
    const endX = x + (i + 1) * nodeSpacing;

    ctx.strokeStyle = colorScheme.textMuted || '#888';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(startX, centerY);
    ctx.lineTo(endX, centerY);
    ctx.stroke();

    // Chain link circles
    const linkX = (startX + endX) / 2;
    ctx.fillStyle = colorScheme.background;
    ctx.strokeStyle = colorScheme.textMuted || '#888';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(linkX, centerY, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  // Draw nodes
  items.forEach((item, i) => {
    const nodeX = x + i * nodeSpacing;
    const nodeY = centerY - nodeSize / 2;

    // Node background
    ctx.fillStyle = colors[i % colors.length];
    roundRect(ctx, nodeX, nodeY, nodeSize, nodeSize, 12);
    ctx.fill();

    // Node border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Icon or number
    const icon = selectIconForContent(typeof item === 'string' ? item : item.label) || 'check-circle';
    renderIcon(ctx, icon, nodeX + nodeSize / 2 - 12, nodeY + 10, 24, '#ffffff');

    // Label
    const text = typeof item === 'string' ? item : (item.label || item.text);
    if (text) {
      ctx.fillStyle = colorScheme.text;
      ctx.font = '500 14px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      const lines = wrapText(ctx, text, nodeSize + 20, 14);
      lines.slice(0, 2).forEach((line, li) => {
        ctx.fillText(line, nodeX + nodeSize / 2, nodeY + nodeSize + 10 + li * 18);
      });
    }
  });
}

/**
 * Render a Versus (comparison) diagram
 */
export function renderVersus(ctx, items, x, y, width, height, colorScheme, options = {}) {
  if (!items || items.length < 2) return;

  const centerX = x + width / 2;
  const padding = width * 0.025;
  const panelWidth = width / 2 - padding * 1.5 - 30;
  const panelHeight = height - padding * 2;
  const titleSize = Math.min(36, height * 0.05);
  const bodySize = Math.min(22, height * 0.03);
  const textPadding = padding * 0.8;

  // Parse a versus item: split string at colon for header:body, or handle object
  function parseItem(item) {
    if (typeof item === 'object') {
      return {
        title: item.label || item.text || '',
        bullets: item.bullets || item.points || [],
        body: '',
      };
    }
    const str = String(item);
    const colonIdx = str.indexOf(':');
    if (colonIdx > 0 && colonIdx < 60) {
      return { title: str.substring(0, colonIdx).trim(), body: str.substring(colonIdx + 1).trim(), bullets: [] };
    }
    return { title: str, body: '', bullets: [] };
  }

  // Word-wrap helper
  function wrapText(text, maxW) {
    const words = text.split(' ');
    let lines = [];
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = word; }
      else line = test;
    }
    if (line) lines.push(line);
    return lines;
  }

  const leftParsed = parseItem(items[0]);
  const rightParsed = parseItem(items[1]);
  // Collect remaining bullets as sub-items for each side
  const extraLeft = items.slice(2).filter((_, i) => i % 2 === 0).map(b => typeof b === 'string' ? b : (b.text || b.label || ''));
  const extraRight = items.slice(2).filter((_, i) => i % 2 === 1).map(b => typeof b === 'string' ? b : (b.text || b.label || ''));

  // Left panel
  ctx.fillStyle = hexToRgba(colorScheme.primary, 0.15);
  roundRect(ctx, x + padding, y + padding, panelWidth, panelHeight, 16);
  ctx.fill();

  // Right panel
  ctx.fillStyle = hexToRgba(colorScheme.secondary, 0.15);
  roundRect(ctx, centerX + 30, y + padding, panelWidth, panelHeight, 16);
  ctx.fill();

  // VS circle
  ctx.fillStyle = colorScheme.accent;
  ctx.beginPath();
  ctx.arc(centerX, y + height / 2, 30, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = `800 ${Math.min(22, height * 0.03)}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('vs', centerX, y + height / 2);

  // Render a side — pre-measure content, then vertically center within panel
  function renderSide(parsed, extraBullets, panelX, accentColor) {
    const maxTextW = panelWidth - textPadding * 2;

    // Pre-measure total content height
    ctx.font = `700 ${titleSize}px Inter, system-ui, sans-serif`;
    const titleLines = wrapText(parsed.title, maxTextW).slice(0, 2);
    let contentH = titleLines.length * titleSize * 1.4 + titleSize * 0.3;

    ctx.font = `400 ${bodySize}px Inter, system-ui, sans-serif`;
    let bodyLines = [];
    if (parsed.body) {
      bodyLines = wrapText(parsed.body, maxTextW - 10).slice(0, 8);
      contentH += bodyLines.length * bodySize * 1.5 + bodySize * 0.3;
    }

    let bulletLinesList = [];
    const allBullets = [...parsed.bullets.slice(0, 6), ...extraBullets.slice(0, 5)];
    if (allBullets.length > 0) {
      allBullets.forEach(bullet => {
        const bLines = wrapText(`\u2022 ${bullet}`, maxTextW - 10);
        bulletLinesList.push(bLines);
        contentH += bLines.length * bodySize * 1.5;
      });
    }

    // Vertically center within panel
    const topOffset = Math.max(0, (panelHeight - contentH) / 2);
    let curY = y + padding + topOffset;
    const maxY = y + padding + panelHeight - textPadding;

    // Title
    ctx.fillStyle = accentColor;
    ctx.font = `700 ${titleSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    titleLines.forEach(line => {
      ctx.fillText(line, panelX + panelWidth / 2, curY);
      curY += titleSize * 1.4;
    });
    curY += titleSize * 0.3;

    // Body text
    ctx.fillStyle = colorScheme.text;
    ctx.font = `400 ${bodySize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'left';
    if (bodyLines.length > 0) {
      bodyLines.forEach(line => {
        if (curY + bodySize > maxY) return;
        ctx.fillText(line, panelX + textPadding, curY);
        curY += bodySize * 1.5;
      });
      curY += bodySize * 0.3;
    }

    // Bullets
    if (bulletLinesList.length > 0) {
      bulletLinesList.forEach(bLines => {
        bLines.forEach(line => {
          if (curY + bodySize > maxY) return;
          ctx.fillText(line, panelX + textPadding, curY);
          curY += bodySize * 1.5;
        });
      });
    }
  }

  renderSide(leftParsed, extraLeft, x + padding, colorScheme.primary);
  renderSide(rightParsed, extraRight, centerX + 30, colorScheme.secondary);
}

/**
 * Render an Iceberg diagram
 */
export function renderIceberg(ctx, items, x, y, width, height, colorScheme, options = {}) {
  const waterlineY = y + height * 0.35;
  const centerX = x + width / 2;

  // Sky/above water background
  const skyGradient = ctx.createLinearGradient(x, y, x, waterlineY);
  skyGradient.addColorStop(0, '#e0f2fe');
  skyGradient.addColorStop(1, '#bae6fd');
  ctx.fillStyle = skyGradient;
  ctx.fillRect(x, y, width, waterlineY - y);

  // Water background
  const waterGradient = ctx.createLinearGradient(x, waterlineY, x, y + height);
  waterGradient.addColorStop(0, '#0ea5e9');
  waterGradient.addColorStop(1, '#0369a1');
  ctx.fillStyle = waterGradient;
  ctx.fillRect(x, waterlineY, width, height - (waterlineY - y));

  // Iceberg shape - above water (small tip)
  const tipWidth = width * 0.25;
  const tipHeight = (waterlineY - y) * 0.7;

  ctx.fillStyle = '#e2e8f0';
  ctx.beginPath();
  ctx.moveTo(centerX, waterlineY - tipHeight);
  ctx.lineTo(centerX + tipWidth / 2, waterlineY);
  ctx.lineTo(centerX - tipWidth / 2, waterlineY);
  ctx.closePath();
  ctx.fill();

  // Iceberg - below water (large mass)
  const baseWidth = width * 0.7;
  const baseHeight = (y + height - waterlineY) * 0.8;

  ctx.fillStyle = hexToRgba('#94a3b8', 0.7);
  ctx.beginPath();
  ctx.moveTo(centerX - tipWidth / 2, waterlineY);
  ctx.lineTo(centerX - baseWidth / 2, waterlineY + baseHeight);
  ctx.lineTo(centerX + baseWidth / 2, waterlineY + baseHeight);
  ctx.lineTo(centerX + tipWidth / 2, waterlineY);
  ctx.closePath();
  ctx.fill();

  // Waterline
  ctx.strokeStyle = '#0284c7';
  ctx.lineWidth = 3;
  ctx.setLineDash([10, 5]);
  ctx.beginPath();
  ctx.moveTo(x, waterlineY);
  ctx.lineTo(x + width, waterlineY);
  ctx.stroke();
  ctx.setLineDash([]);

  // Labels
  if (items && items.length >= 2) {
    const visibleText = typeof items[0] === 'string' ? items[0] : items[0].label;
    const hiddenText = typeof items[1] === 'string' ? items[1] : items[1].label;

    // Visible label (above water)
    ctx.fillStyle = '#1e293b';
    ctx.font = '700 20px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(visibleText || 'Visible', centerX, y + 40);

    // Hidden label (below water)
    ctx.fillStyle = '#ffffff';
    ctx.fillText(hiddenText || 'Hidden', centerX, waterlineY + baseHeight / 2);
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function hexToRgba(hex, alpha) {
  if (!hex) return `rgba(128, 128, 128, ${alpha})`;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(128, 128, 128, ${alpha})`;
  return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
}

function adjustColor(hex, amount) {
  if (!hex) return '#888888';
  let color = hex.replace('#', '');
  if (color.length === 3) {
    color = color.split('').map(c => c + c).join('');
  }

  const num = parseInt(color, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;

  r = Math.min(255, Math.max(0, r));
  g = Math.min(255, Math.max(0, g));
  b = Math.min(255, Math.max(0, b));

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function drawArrowHead(ctx, x, y, direction) {
  const size = 8;
  ctx.fillStyle = ctx.strokeStyle;
  ctx.beginPath();

  switch (direction) {
    case 'up':
      ctx.moveTo(x, y);
      ctx.lineTo(x - size, y + size);
      ctx.lineTo(x + size, y + size);
      break;
    case 'down':
      ctx.moveTo(x, y);
      ctx.lineTo(x - size, y - size);
      ctx.lineTo(x + size, y - size);
      break;
    case 'left':
      ctx.moveTo(x, y);
      ctx.lineTo(x + size, y - size);
      ctx.lineTo(x + size, y + size);
      break;
    case 'right':
      ctx.moveTo(x, y);
      ctx.lineTo(x - size, y - size);
      ctx.lineTo(x - size, y + size);
      break;
  }

  ctx.closePath();
  ctx.fill();
}

function drawArrowHeadAtAngle(ctx, x, y, angle, color) {
  const size = 10;
  ctx.fillStyle = color;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(0, -size);
  ctx.lineTo(-size / 2, 0);
  ctx.lineTo(size / 2, 0);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function wrapText(ctx, text, maxWidth, fontSize) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);

  return lines;
}

export default {
  renderFunnel,
  renderPyramid,
  renderVenn,
  renderQuadrant,
  renderBullseye,
  renderCycle,
  renderStaircase,
  renderChain,
  renderVersus,
  renderIceberg
};
